// AddDeviceDialog – add device rows to the device mappings master table, either as a
// single row (one field per column currently present in the live DynamoDB table) or
// as a bulk CSV upload.

import React, { JSX } from 'react';
import { PortaledLayer } from '../../shared/PortaledLayer';
import { deviceMappingColumnLabel } from '../../../helpers/deviceMappingRows';
import InlineSpinner from '../../shared/InlineSpinner';

// ---- Example table (bulk CSV tab) ----

const EXAMPLE_CSV_COLUMNS = [
  'name',
  'deviceRole',
  'pairName',
  'macFront',
  'macRear',
  'athleteId',
  'athleteNumber',
  'eventId',
  'raceId',
  'draftingThresholdSeconds',
  'description',
  'simId',
  'iccid',
] as const;

const EXAMPLE_CSV_ROWS: Record<(typeof EXAMPLE_CSV_COLUMNS)[number], string>[] = [
  {
    name: 'Rear_1234A',
    deviceRole: 'rear',
    pairName: 'Front_1234A',
    macFront: 'AA:BB:CC:11:22:01',
    macRear: 'AA:BB:CC:11:22:02',
    athleteId: 'ath-042',
    athleteNumber: '42',
    eventId: 'evt-2026-summer',
    raceId: 'race-5k',
    draftingThresholdSeconds: '3',
    description: 'waterproofed',
    simId: 'SIM-00123',
    iccid: '8944100001234567890',
  },
  {
    name: 'Front_1234A',
    deviceRole: 'front',
    pairName: 'Rear_1234A',
    macFront: '',
    macRear: '',
    athleteId: 'ath-042',
    athleteNumber: '42',
    eventId: 'evt-2026-summer',
    raceId: 'race-5k',
    draftingThresholdSeconds: '',
    description: '',
    simId: '',
    iccid: '',
  },
];

// ---- Types ----

export type AddDeviceMode = 'single' | 'bulk';

type AddDeviceDialogProps = {
  open: boolean;
  mode: AddDeviceMode;
  onModeChange: (mode: AddDeviceMode) => void;

  // Single-device form.
  columns: string[];
  columnsLoading: boolean;
  values: Record<string, string>;
  onValueChange: (column: string, value: string) => void;
  saving: boolean;
  onSubmit: () => void;

  // Bulk CSV upload.
  bulkFileName: string;
  bulkRowCount: number;
  bulkError: string;
  bulkUploading: boolean;
  onBulkFileSelected: (file: File) => void;
  onBulkSubmit: () => void;

  onCancel: () => void;
};

// ---- Component ----

export function AddDeviceDialog(props: AddDeviceDialogProps): JSX.Element | null {
  const {
    open,
    mode,
    onModeChange,
    columns,
    columnsLoading,
    values,
    onValueChange,
    saving,
    onSubmit,
    bulkFileName,
    bulkRowCount,
    bulkError,
    bulkUploading,
    onBulkFileSelected,
    onBulkSubmit,
    onCancel,
  } = props;

  if (!open) return null;

  const deviceIdValue = (values.name ?? '').trim();
  const roleValue = values.deviceRole ?? '';
  const roleValid = roleValue === 'front' || roleValue === 'rear';
  const isSingle = mode === 'single';
  const busy = saving || bulkUploading;
  const canSubmitSingle = !!deviceIdValue && roleValid && !saving && !columnsLoading;

  const tabClass = (active: boolean): string =>
    `px-3 py-1.5 text-xs font-medium rounded-md ${
      active
        ? 'bg-blue-600 text-white'
        : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 dark:bg-slate-900 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-800'
    }`;

  return (
    <PortaledLayer>
      <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4 dark:bg-black/70">
        <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-sm text-gray-800 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
            <div className="font-semibold text-gray-900 dark:text-slate-100">Add device</div>
            <div className="flex gap-1.5">
              <button type="button" className={tabClass(isSingle)} disabled={busy} onClick={() => onModeChange('single')}>
                Single device
              </button>
              <button type="button" className={tabClass(!isSingle)} disabled={busy} onClick={() => onModeChange('bulk')}>
                Bulk upload (CSV)
              </button>
            </div>
          </div>

          {isSingle ? (
            <div className="space-y-3 overflow-y-auto px-4 py-3">
              <p className="text-xs text-gray-600 dark:text-slate-400">
                Adds a new row to the device mappings table (DynamoDB). This affects device
                representation across the entire business. Device ID and Role are required; every
                other field is optional.
              </p>

              {columnsLoading ? (
                <div className="text-xs text-gray-500 dark:text-slate-400">Loading current table columns…</div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {columns.map((col) => {
                    const isDeviceId = col === 'name';
                    const isRole = col === 'deviceRole';
                    const isRequired = isDeviceId || isRole;
                    const fieldId = `add-device-field-${col}`;
                    return (
                      <div key={col} className={isDeviceId ? 'sm:col-span-2' : undefined}>
                        <label
                          htmlFor={fieldId}
                          className="mb-1 block text-xs font-medium text-gray-700 dark:text-slate-300"
                        >
                          {deviceMappingColumnLabel(col)}
                          {isRequired ? ' *' : ''}
                        </label>
                        {isRole ? (
                          <select
                            id={fieldId}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            value={roleValue}
                            disabled={saving}
                            onChange={(e) => onValueChange(col, e.target.value)}
                          >
                            <option value="">Select role…</option>
                            <option value="front">front</option>
                            <option value="rear">rear</option>
                          </select>
                        ) : (
                          <input
                            id={fieldId}
                            autoFocus={isDeviceId}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                            placeholder={isDeviceId ? 'e.g. Rear_1234A' : ''}
                            value={values[col] ?? ''}
                            disabled={saving}
                            onChange={(e) => onValueChange(col, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && canSubmitSingle) onSubmit();
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 overflow-y-auto px-4 py-3">
              <p className="text-xs text-gray-600 dark:text-slate-400">
                Upload a CSV to add many devices at once. The first row must be a header with a{' '}
                <code className="rounded bg-gray-100 px-1 dark:bg-slate-800">name</code> (device ID) column;
                any other columns match the mappings table&apos;s existing fields (e.g.{' '}
                <code className="rounded bg-gray-100 px-1 dark:bg-slate-800">deviceRole</code>,{' '}
                <code className="rounded bg-gray-100 px-1 dark:bg-slate-800">pairName</code>,{' '}
                <code className="rounded bg-gray-100 px-1 dark:bg-slate-800">athleteId</code>, …). Devices whose
                ID already exists in the table are skipped.
              </p>

              <div className="rounded-md border border-gray-200 bg-gray-50 p-2 text-xs dark:border-slate-700 dark:bg-slate-800">
                <p className="mb-1 font-semibold text-gray-700 dark:text-slate-300">Example:</p>
                <div className="overflow-x-auto rounded border border-gray-200 dark:border-slate-700">
                  <table className="w-max min-w-full border-separate border-spacing-0 text-[11px]">
                    <thead className="bg-gray-100 dark:bg-slate-900">
                      <tr>
                        {EXAMPLE_CSV_COLUMNS.map((col) => (
                          <th
                            key={col}
                            className="whitespace-nowrap border-b border-gray-200 p-1.5 text-left font-semibold text-gray-700 dark:border-slate-700 dark:text-slate-300"
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {EXAMPLE_CSV_ROWS.map((row, i) => (
                        <tr key={i} className="odd:bg-white even:bg-gray-50 dark:odd:bg-slate-800 dark:even:bg-slate-800/60">
                          {EXAMPLE_CSV_COLUMNS.map((col) => (
                            <td
                              key={col}
                              className="whitespace-nowrap border-b border-gray-200 p-1.5 text-gray-700 dark:border-slate-700 dark:text-slate-300"
                            >
                              {row[col] || <span className="text-gray-300 dark:text-slate-600">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">
                Choose CSV file
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  disabled={bulkUploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onBulkFileSelected(file);
                    e.target.value = '';
                  }}
                />
              </label>

              {bulkFileName && (
                <div className="text-xs text-gray-600 dark:text-slate-400">
                  File: <span className="font-medium text-gray-800 dark:text-slate-200">{bulkFileName}</span>
                </div>
              )}

              {bulkError ? (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                  {bulkError}
                </div>
              ) : bulkRowCount > 0 ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200">
                  Parsed {bulkRowCount} device{bulkRowCount === 1 ? '' : 's'} from the file.
                </div>
              ) : null}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3 text-xs dark:border-slate-700 dark:bg-slate-800">
            <button
              type="button"
              className="rounded border border-gray-300 bg-white px-3 py-1 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              onClick={onCancel}
              disabled={busy}
            >
              Cancel
            </button>
            {isSingle ? (
              <button
                type="button"
                className="inline-flex min-w-[6.5rem] items-center justify-center px-4 py-1 rounded bg-blue-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                disabled={!canSubmitSingle}
                onClick={onSubmit}
                aria-busy={saving}
                aria-label={saving ? 'Adding device' : undefined}
              >
                {saving ? <InlineSpinner size="xs" className="border-white border-t-transparent" /> : 'Add device'}
              </button>
            ) : (
              <button
                type="button"
                className="inline-flex min-w-[8rem] items-center justify-center px-4 py-1 rounded bg-blue-600 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                disabled={bulkRowCount === 0 || !!bulkError || bulkUploading}
                onClick={onBulkSubmit}
                aria-busy={bulkUploading}
                aria-label={bulkUploading ? 'Uploading devices' : undefined}
              >
                {bulkUploading ? (
                  <InlineSpinner size="xs" className="border-white border-t-transparent" />
                ) : (
                  `Upload ${bulkRowCount || ''} device${bulkRowCount === 1 ? '' : 's'}`
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </PortaledLayer>
  );
}
